import React, { useState } from 'react';
import { Loader2, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

const REVIEW_SECTIONS = [
  { key: 'invalid_rows', titleKey: 'invalidRows' },
  { key: 'contactado_defaulted_whatsapp', titleKey: 'contactadoDefaultedWhatsapp' },
  { key: 'follow_up_no_date', titleKey: 'followUpNoDate' },
  { key: 'address_unparsed', titleKey: 'addressUnparsed' },
  { key: 'unrecognized_statuses', titleKey: 'unrecognizedStatuses' },
];

function RowRefList({ rows, t }) {
  return (
    <ul className="mt-1.5 space-y-0.5 max-h-32 overflow-y-auto">
      {rows.map((row, i) => (
        <li key={i} className="text-xs text-zinc-500">
          {t('aiLeadScanner.importModal.rowRef', { sheet: row.sheet, row: row.row })}
          {row.value ? ` — "${row.value}"` : ''}
        </li>
      ))}
    </ul>
  );
}

// Doble confirmación no negociable (Commit 12, mismo patrón que usamos
// nosotros mismos con el Excel real de Marta): dry_run=true primero,
// resumen completo legible, solo entonces "Confirmar import" repite la
// misma llamada con dry_run=false sobre el MISMO archivo ya seleccionado
// (no se vuelve a pedir el archivo). Nunca se salta este paso.
export function ImportExcelModal({ open, onOpenChange, onImported }) {
  const { api } = useAuth();
  const { t } = useTranslation();

  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [dryRunSummary, setDryRunSummary] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const resetAndClose = () => {
    setFile(null);
    setDryRunSummary(null);
    onOpenChange(false);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setDryRunSummary(null);
  };

  const runImport = async (dryRun) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/leads/import-excel', formData, { params: { dry_run: dryRun } });
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const res = await runImport(true);
      setDryRunSummary(res.data);
    } catch (error) {
      console.error('Excel dry-run failed:', error);
      toast.error(t('aiLeadScanner.importModal.error'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await runImport(false);
      toast.success(t('aiLeadScanner.importModal.success', { count: res.data.imported }));
      onImported?.();
      resetAndClose();
    } catch (error) {
      console.error('Excel real import failed:', error);
      toast.error(t('aiLeadScanner.importModal.error'));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) resetAndClose(); else onOpenChange(next); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('aiLeadScanner.actions.importExcel')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Input type="file" accept=".xlsx" onChange={handleFileChange} data-testid="import-excel-file-input" />
          </div>

          {!dryRunSummary && (
            <Button
              onClick={handleAnalyze}
              disabled={!file || analyzing}
              className="w-full"
              data-testid="import-excel-analyze"
            >
              {analyzing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {t('aiLeadScanner.importModal.runDryRun')}
            </Button>
          )}

          {dryRunSummary && (
            <>
              <div className="bg-zinc-800/50 rounded-lg p-3 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-zinc-500">{t('aiLeadScanner.importModal.totalRows')}:</span>{' '}
                  <span className="text-zinc-200 font-medium">{dryRunSummary.total_rows}</span>
                </div>
                <div>
                  <span className="text-zinc-500">{t('aiLeadScanner.importModal.imported')}:</span>{' '}
                  <span className="text-emerald-400 font-medium">{dryRunSummary.imported}</span>
                </div>
                <div>
                  <span className="text-zinc-500">{t('aiLeadScanner.importModal.duplicates')}:</span>{' '}
                  <span className="text-zinc-200 font-medium">{dryRunSummary.duplicates}</span>
                </div>
              </div>

              {REVIEW_SECTIONS.map(({ key, titleKey }) => {
                const rows = dryRunSummary[key] || [];
                if (rows.length === 0) return null;
                return (
                  <div key={key} className="border-t border-zinc-800 pt-2.5">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {t(`aiLeadScanner.importModal.${titleKey}`)} ({rows.length})
                    </div>
                    <RowRefList rows={rows} t={t} />
                  </div>
                );
              })}

              <div className="flex items-start gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-2.5">
                <CheckCircle2 className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-indigo-300">{t('aiLeadScanner.importModal.confirmWarning')}</p>
              </div>

              <Button
                onClick={handleConfirm}
                disabled={confirming}
                className="w-full"
                data-testid="import-excel-confirm"
              >
                {confirming && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {t('aiLeadScanner.importModal.confirmImport')}
              </Button>
            </>
          )}

          <div className="flex justify-end">
            <Button variant="ghost" onClick={resetAndClose} disabled={analyzing || confirming}>
              {t('aiLeadScanner.leadDetail.cancel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
